import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import {
  ArrowRight, CheckCircle, Monitor, HardDrive, Server,
  Shield, Zap, FileText, Bell, Eye, Download
} from "lucide-react";

const VEEAM_LOGO = "https://static.prod-images.emergentagent.com/jobs/88962dbc-a235-4c0e-8a99-4ab3319f3e79/images/db6206c33f887dc55e7c8703b073de79c2397c16569dc695943f88a362e7feef.jpeg";
const IPERIUS_LOGO = "https://static.prod-images.emergentagent.com/jobs/88962dbc-a235-4c0e-8a99-4ab3319f3e79/images/caa7a465deaed7e9f8c40e232aea446a267a7046e1a568d6364930d299606e25.jpeg";
const WSB_LOGO = "https://static.prod-images.emergentagent.com/jobs/88962dbc-a235-4c0e-8a99-4ab3319f3e79/images/faa8f05aaae1ddf3bb4025c31c4f6130bc38e116bedb39f1111ccc3cc0f0f734.jpeg";
const HERO_BG = "https://images.unsplash.com/photo-1639066648921-82d4500abf1a?auto=format&fit=crop&w=1920&q=80";

const agents = [
  {
    id: "veeam",
    name: "Veeam Backup & Replication",
    logo: VEEAM_LOGO,
    color: "emerald",
    tagline: "Le leader mondial de la sauvegarde et de la replication",
    description: "ServeurOnline surveille en temps reel vos jobs Veeam Backup & Replication. L'agent collecte automatiquement les resultats de chaque session de sauvegarde, y compris les environnements multi-VM, et les transmet a votre tableau de bord.",
    monitored: [
      "Statut de chaque job de sauvegarde (succes, avertissement, erreur)",
      "Detail par machine virtuelle (multi-VM natif)",
      "Taille des sauvegardes et duree d'execution",
      "Nom du job et cible de sauvegarde",
      "Historique complet des sessions",
      "Messages d'erreur detailles en cas d'echec",
    ],
    features: [
      { icon: Monitor, text: "Supervision multi-VM avec detail par VM" },
      { icon: Bell, text: "Alertes email en temps reel sur echec" },
      { icon: FileText, text: "Rapports PDF/CSV par serveur ou client" },
      { icon: Eye, text: "Monitoring public pour vos clients" },
    ],
    howItWorks: [
      { step: "01", title: "Creez le serveur", desc: "Ajoutez le serveur dans ServeurOnline et selectionnez l'agent Veeam. Une cle API est generee automatiquement." },
      { step: "02", title: "Deployez l'agent", desc: "Telechargez le package agent (ZIP) contenant le script PowerShell. Executez-le sur votre serveur Windows ou planifiez-le avec le Planificateur de taches." },
      { step: "03", title: "Surveillez tout", desc: "L'agent lit les sessions Veeam via PowerShell (Get-VBRBackupSession) et transmet les resultats. Tout apparait dans votre dashboard en temps reel." },
    ],
    techDetails: [
      "Agent PowerShell natif Windows — aucune installation tierce requise",
      "Compatible Veeam Backup & Replication v10, v11, v12+",
      "Lecture des sessions via le module PowerShell Veeam (VeeamPSSnapin)",
      "Communication HTTPS securisee vers l'API ServeurOnline",
      "Planification via le Planificateur de taches Windows (toutes les heures recommande)",
      "Empreinte memoire negligeable — aucun service resident",
    ],
    image: "https://images.pexels.com/photos/1181335/pexels-photo-1181335.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "iperius",
    name: "Iperius Backup",
    logo: IPERIUS_LOGO,
    color: "amber",
    tagline: "Sauvegarde polyvalente pour serveurs et postes de travail",
    description: "ServeurOnline analyse les logs XML d'Iperius Backup pour extraire les resultats de chaque operation de sauvegarde. L'agent detecte automatiquement les fichiers de log et transmet les statuts a votre plateforme de monitoring.",
    monitored: [
      "Statut de chaque operation de sauvegarde",
      "Nom du job et type de sauvegarde",
      "Date et heure de la derniere sauvegarde",
      "Taille des donnees sauvegardees",
      "Duree de l'operation",
      "Messages d'erreur ou d'avertissement",
    ],
    features: [
      { icon: Monitor, text: "Dashboard centralise multi-clients" },
      { icon: Bell, text: "Notifications sur echec de sauvegarde" },
      { icon: FileText, text: "Historique detaille exportable" },
      { icon: Shield, text: "Isolation complete des donnees (multi-tenant)" },
    ],
    howItWorks: [
      { step: "01", title: "Creez le serveur", desc: "Ajoutez le serveur dans ServeurOnline et selectionnez l'agent Iperius. Recuperez la cle API generee." },
      { step: "02", title: "Deployez l'agent", desc: "Telechargez le package agent et placez le script PowerShell sur le serveur. Il analyse les logs XML d'Iperius automatiquement." },
      { step: "03", title: "Surveillez tout", desc: "L'agent parse les fichiers de log Iperius et envoie les resultats. Consultez l'etat de toutes vos sauvegardes Iperius depuis un seul dashboard." },
    ],
    techDetails: [
      "Agent PowerShell natif Windows — aucune dependance externe",
      "Compatible Iperius Backup (toutes editions : Free, Desktop, Full, Advanced)",
      "Analyse des fichiers de log XML dans le repertoire Iperius",
      "Communication HTTPS securisee vers l'API ServeurOnline",
      "Planification recommandee : toutes les heures via le Planificateur de taches",
      "Aucun impact sur les performances du serveur",
    ],
    image: "https://images.unsplash.com/photo-1584169417032-d34e8d805e8b?auto=format&fit=crop&w=940&q=80",
  },
  {
    id: "wsb",
    name: "Windows Server Backup",
    logo: WSB_LOGO,
    color: "blue",
    tagline: "La solution de sauvegarde native de Windows Server",
    description: "ServeurOnline interroge directement Windows Server Backup via PowerShell pour extraire l'etat des sauvegardes. Aucun logiciel tiers necessaire — l'agent exploite les cmdlets natifs wbadmin pour remonter les resultats en temps reel.",
    monitored: [
      "Statut de la derniere sauvegarde (succes, echec)",
      "Date et heure de la derniere sauvegarde reussie",
      "Volumes et disques inclus dans la sauvegarde",
      "Destination de la sauvegarde (disque local, reseau, etc.)",
      "Duree de la derniere operation",
      "Messages d'erreur Windows en cas d'echec",
    ],
    features: [
      { icon: Monitor, text: "Supervision sans installation tierce" },
      { icon: Bell, text: "Alerte si la derniere sauvegarde a echoue" },
      { icon: Shield, text: "Compatible toutes editions Windows Server" },
      { icon: FileText, text: "Historique et rapports exportables" },
    ],
    howItWorks: [
      { step: "01", title: "Creez le serveur", desc: "Ajoutez le serveur dans ServeurOnline et selectionnez l'agent Windows Server Backup. Recuperez la cle API generee." },
      { step: "02", title: "Deployez l'agent", desc: "Telechargez le package agent. Le script PowerShell utilise les cmdlets natifs Get-WBSummary et Get-WBJob pour lire les resultats de sauvegarde." },
      { step: "03", title: "Surveillez tout", desc: "L'agent remonte automatiquement le statut, la date et les details de chaque sauvegarde Windows Server Backup vers votre dashboard." },
    ],
    techDetails: [
      "Agent PowerShell natif — utilise les cmdlets Windows Server Backup (wbadmin, Get-WBSummary)",
      "Compatible Windows Server 2012 R2, 2016, 2019, 2022, 2025",
      "Necessite le role 'Windows Server Backup' installe sur le serveur",
      "Communication HTTPS securisee vers l'API ServeurOnline",
      "Planification recommandee : toutes les heures via le Planificateur de taches",
      "Aucune dependance externe — 100% natif Windows",
    ],
    image: "https://images.pexels.com/photos/17489151/pexels-photo-17489151.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

const colorMap = {
  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", badge: "bg-emerald-500/20 text-emerald-400" },
  amber: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", badge: "bg-amber-500/20 text-amber-400" },
  blue: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-400", badge: "bg-blue-500/20 text-blue-400" },
};

const AgentsPublic = () => {
  return (
    <div className="min-h-screen bg-[#07090E]">
      <PublicNavbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#07090E]/60 via-[#07090E]/80 to-[#07090E]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400/80 mb-4">Agents compatibles</p>
          <h1
            className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Les logiciels que nous{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">
              surveillons
            </span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            ServeurOnline dispose d'agents legers pour les principaux logiciels de sauvegarde.
            Un simple script PowerShell a deployer, et c'est tout.
          </p>

          {/* Agent cards preview */}
          <div className="flex flex-wrap justify-center gap-6 mt-12">
            {agents.map((agent) => {
              const c = colorMap[agent.color];
              return (
                <a
                  key={agent.id}
                  href={`#${agent.id}`}
                  className={`group flex items-center gap-4 px-6 py-4 rounded-2xl border ${c.border} ${c.bg} hover:scale-[1.02] transition-all`}
                  data-testid={`agent-card-${agent.id}`}
                >
                  <img src={agent.logo} alt={agent.name} className="w-14 h-14 rounded-xl object-cover" />
                  <div className="text-left">
                    <p className="text-white font-semibold">{agent.name}</p>
                    <p className="text-slate-500 text-sm">{agent.tagline}</p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Agent details */}
      {agents.map((agent, idx) => {
        const c = colorMap[agent.color];
        return (
          <section
            key={agent.id}
            id={agent.id}
            className={`py-20 sm:py-28 ${idx % 2 === 1 ? "bg-[#0D111A]/30" : ""}`}
            data-testid={`agent-section-${agent.id}`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Header */}
              <div className="flex flex-col lg:flex-row items-start gap-8 mb-20">
                <div className={`w-24 h-24 rounded-2xl border ${c.border} ${c.bg} p-2 flex-shrink-0 overflow-hidden`}>
                  <img src={agent.logo} alt={agent.name} className="w-full h-full object-cover rounded-xl" />
                </div>
                <div className="flex-1">
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${c.badge} text-xs font-mono mb-3`}>
                    <span className="relative flex h-2 w-2">
                      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${agent.color === "emerald" ? "bg-emerald-400" : "bg-amber-400"} opacity-75`}></span>
                      <span className={`relative inline-flex rounded-full h-2 w-2 ${agent.color === "emerald" ? "bg-emerald-500" : "bg-amber-500"}`}></span>
                    </span>
                    Agent disponible
                  </div>
                  <h2
                    className="text-3xl sm:text-4xl font-bold text-white mb-6"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {agent.name}
                  </h2>
                  <p className="text-slate-400 text-lg leading-relaxed max-w-2xl">{agent.description}</p>
                </div>
              </div>

              {/* Photo */}
              <div className={`rounded-2xl border ${c.border} overflow-hidden mb-20 max-h-[280px]`}>
                <img src={agent.image} alt={agent.name} className="w-full h-[280px] object-cover" loading="lazy" />
              </div>

              {/* What we monitor */}
              <div className="grid lg:grid-cols-2 gap-16 mb-20">
                <div>
                  <h3 className={`text-xl font-semibold text-white mb-6 flex items-center gap-3`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    <Monitor className={c.text} size={22} />
                    Ce que nous surveillons
                  </h3>
                  <div className="space-y-4">
                    {agent.monitored.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle className={`${c.text} flex-shrink-0 mt-0.5`} size={16} />
                        <span className="text-slate-300 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    <Zap className={c.text} size={22} />
                    Fonctionnalites
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {agent.features.map((feat, i) => (
                      <div key={i} className={`p-5 rounded-xl border ${c.border} ${c.bg}`}>
                        <feat.icon className={`${c.text} mb-3`} size={20} />
                        <p className="text-sm text-slate-300">{feat.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* How it works */}
              <div className="mb-20">
                <h3 className="text-xl font-semibold text-white mb-10 flex items-center gap-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  <Download className={c.text} size={22} />
                  Comment ca marche
                </h3>
                <div className="grid md:grid-cols-3 gap-8">
                  {agent.howItWorks.map((step, i) => (
                    <div key={i} className={`relative p-8 rounded-2xl border ${c.border} bg-[#0D111A]/70`}>
                      <span className="text-4xl font-bold text-slate-800/50 absolute top-4 right-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        {step.step}
                      </span>
                      <h4 className="text-white font-semibold mb-3">{step.title}</h4>
                      <p className="text-slate-400 text-sm leading-relaxed">{step.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tech details */}
              <div className={`p-10 rounded-2xl border ${c.border} ${c.bg}`}>
                <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  <HardDrive className={c.text} size={20} />
                  Details techniques
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {agent.techDetails.map((detail, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className={`${c.text} mt-1.5 flex-shrink-0`}>&#x2022;</span>
                      <span className="text-slate-400 text-sm">{detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="p-10 sm:p-16 rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-[#0D111A] to-[#0D1520]" style={{ boxShadow: "0 0 80px rgba(6, 182, 212, 0.08)" }}>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Pret a surveiller vos sauvegardes ?
            </h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Deployez un agent en moins de 5 minutes. Essai gratuit de 14 jours, sans carte bancaire.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-8 py-6 text-base rounded-xl shadow-lg shadow-cyan-500/20" data-testid="agents-cta-register">
                  Commencer l'essai gratuit
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="ghost" className="text-slate-300 hover:text-white border border-slate-700 px-8 py-6 text-base rounded-xl" data-testid="agents-cta-contact">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AgentsPublic;
